import React, { useState, useContext } from "react";
import { Container, Form, Button, Spinner, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import "../../../assets/css/Customer/CustomerSettings/CustomerSecurity.css";
import CustomerSettingsMasterLayout from "../../../layouts/Customer/CustomerSettingsMasterLayout";
import AppContext from "../../../context/AppContext";
import axios from "axios";

const CustomerSecurity = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [formData, setFormData] = useState({
    previousPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    previous: false,
    new: false,
    confirm: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleUpdatePassword = () => {
    // Validation
    if (
      !formData.previousPassword ||
      !formData.newPassword ||
      !formData.confirmNewPassword
    ) {
      alert("Please fill all fields");
      return;
    }

    if (formData.newPassword !== formData.confirmNewPassword) {
      alert("New password and confirm password do not match");
      return;
    }

    if (formData.newPassword.length < 8) {
      alert("Password should be at least 8 characters");
      return;
    }

    setLoading(true);

    axios
      .post(
        `${baseUrl}/customer/profile/changePassword`,
        {
          previous_password: formData.previousPassword,
          new_password: formData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      )
      .then((response) => {
        setLoading(false);

        if (response.data.success === true) {
          // Clear form fields
          setFormData({
            previousPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          });

          // Show the celebration modal (no blocking alert here!)
          setShowSuccessModal(true);

          // Give the user time to see the modal, then navigate
          setTimeout(() => {
            setShowSuccessModal(false);
            navigate("/customer-dashboard");
          }, 2200);
        } else {
          alert(response.data.message || "Wrong password");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        alert("Network Error! Please try again.");
      });
  };

  return (
    <CustomerSettingsMasterLayout
      screenName={"CustomerSecurity"}
      centered={true}
    >
      <div className="cs-security-container">
        <Container className="cs-container">
          {/* Security Form */}
          <div className="cs-form-section">
            <div className="cs-form-header">
              <div className="cs-lock-icon-wrapper">
                <FaLock className="cs-lock-icon" />
              </div>
              <h6 className="cs-form-title">Change Password</h6>
              <p className="cs-form-description">
                Please enter your current password and create a new secure
                password
              </p>
            </div>

            {/* Previous Password */}
            <Form.Group className="cs-form-group">
              <Form.Label className="cs-form-label">
                Previous Password
              </Form.Label>
              <div className="cs-password-input-wrapper">
                <FaLock className="cs-input-icon" />
                <Form.Control
                  type={showPassword.previous ? "text" : "password"}
                  name="previousPassword"
                  value={formData.previousPassword}
                  onChange={handleInputChange}
                  placeholder="Enter previous password"
                  className="cs-form-input"
                />
                <button
                  type="button"
                  className="cs-eye-btn"
                  onClick={() => togglePasswordVisibility("previous")}
                >
                  {showPassword.previous ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </Form.Group>

            {/* New Password */}
            <Form.Group className="cs-form-group">
              <Form.Label className="cs-form-label">New Password</Form.Label>
              <div className="cs-password-input-wrapper">
                <FaLock className="cs-input-icon" />
                <Form.Control
                  type={showPassword.new ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  className="cs-form-input"
                />
                <button
                  type="button"
                  className="cs-eye-btn"
                  onClick={() => togglePasswordVisibility("new")}
                >
                  {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </Form.Group>

            {/* Confirm New Password */}
            <Form.Group className="cs-form-group">
              <Form.Label className="cs-form-label">
                Confirm New Password
              </Form.Label>
              <div className="cs-password-input-wrapper">
                <FaLock className="cs-input-icon" />
                <Form.Control
                  type={showPassword.confirm ? "text" : "password"}
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleInputChange}
                  placeholder="Re-enter new password"
                  className="cs-form-input"
                />
                <button
                  type="button"
                  className="cs-eye-btn"
                  onClick={() => togglePasswordVisibility("confirm")}
                >
                  {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </Form.Group>

            {/* Update Button */}
            <Button
              type="button"
              className="cs-update-btn"
              onClick={handleUpdatePassword}
              disabled={loading}
            >
              {loading ? (
                <div className="btn-content-wrapper">
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Loading...</span>
                </div>
              ) : (
                "Update Password"
              )}
            </Button>

            {/* Success Celebration Modal */}
            <Modal
              show={showSuccessModal}
              centered
              backdrop="static"
              className="booking-success-modal"
            >
              <Modal.Body className="text-center p-5">
                <div className="cs-success-modal__emoji">🎉</div>
                <h3 className="cs-success-modal__title">Congratulations!</h3>
                <p className="cs-success-modal__text">
                  Your password has been updated successfully. <br />
                </p>
                <div
                  className="spinner-border mt-3 cs-success-modal__spinner"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </Modal.Body>
            </Modal>
          </div>
        </Container>
      </div>
    </CustomerSettingsMasterLayout>
  );
};

export default CustomerSecurity;
